/* Groups — the register that answers "who can get to what".
 *
 * The people register already tells you Carl Cooper is in eleven groups. It
 * cannot tell you who else is in them, and that is the question that matters
 * when somebody leaves, or when an audit asks who could reach the finance share.
 * This screen is that question answered from the other end.
 *
 * Membership is read-only, deliberately, and the drawer says so. This register
 * records access; it does not grant it. A screen that let you remove somebody
 * from a security group would be a screen that quietly failed to — the write
 * would go to a SharePoint list, not to Entra, and the sync would put it back at
 * 02:00 having changed nothing about what that person could actually open.
 *
 * Company-wide, like Licences. A group is not owned by a depot.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fmtDate } from '../../lib/format';
import type { IGroup, IGroupMember, IPerson, Tone } from '../../models';
import { recordHref, useOpenRecord } from '../../hooks/useOpenRecord';
import { isLeaver } from '../../lib/registers';
import { qk } from '../../query/keys';
import { useServices } from '../../services/context';
import {
  Badge,
  Controls,
  DataTable,
  EmptyState,
  KeyValue,
  LoadError,
  Capped,
  Panel,
  SearchBox,
  Section,
  Segmented,
  SummaryTile,
  SummaryTiles,
  Select,
  StatCard,
  StatGrid,
  TableSkeleton,
  type Column,
} from '../ui';
import { EditDrawer } from './EditDrawer';
import { Explain } from './Explain';
import { groupFields } from '../../lib/registerFields';
import s from './screens.module.scss';

const GROUP_TYPES = [
  'Security',
  'Mail-enabled security',
  'Distribution',
  'Microsoft 365',
  'Dynamic',
] as const;

/** Type → badge tone. Security and Dynamic lead because they are the two that
    grant access without anyone necessarily having added a member by hand. */
const TYPE_TONE: Record<string, Tone> = {
  Security: 'purple',
  'Mail-enabled security': 'purple',
  Dynamic: 'info',
  'Microsoft 365': 'muted',
  Distribution: 'muted',
};

interface Row extends IGroup {
  /** Members resolved against the People register, in name order. */
  people: IPerson[];
  /** Members with no row here — nested groups, or accounts the sync excludes. */
  unresolved: IGroupMember[];
  leavers: IPerson[];
}

export function GroupsScreen() {
  const svc = useServices();

  const [query, setQuery] = useState('');
  const [type, setType] = useState('all');
  const [lens, setLens] = useState('all');
  const [selected, setSelected] = useState<Row | null>(null);

  const qc = useQueryClient();
  const groups = useQuery({ queryKey: qk.groups, queryFn: () => svc.groups.all() });
  const people = useQuery({ queryKey: qk.people, queryFn: () => svc.people.list() });

  const rows = useMemo<Row[]>(() => {
    /* Joined on Graph's object id, which the sync writes to both registers. Not
       on name: two people can share one, and a renamed account would silently
       drop out of every group it is in. */
    const byGraphId = new Map(
      (people.data ?? []).filter((p) => p.graphId).map((p) => [p.graphId!, p]),
    );

    return (groups.data ?? []).map((g) => {
      const resolved: IPerson[] = [];
      const unresolved: IGroupMember[] = [];
      for (const m of g.members) {
        const person = byGraphId.get(m.id);
        if (person) resolved.push(person);
        else unresolved.push(m);
      }
      resolved.sort((a, b) => a.name.localeCompare(b.name));
      return {
        ...g,
        people: resolved,
        unresolved,
        // A blocked service account in a group is not a leaver left behind.
        leavers: resolved.filter(isLeaver),
      };
    });
  }, [groups.data, people.data]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((g) => {
      if (type !== 'all' && g.type !== type) return false;
      if (lens === 'undescribed' && g.purpose.trim()) return false;
      if (lens === 'leavers' && g.leavers.length === 0) return false;
      if (lens === 'empty' && g.memberCount > 0) return false;
      if (lens === 'unowned' && g.owners.length > 0) return false;
      if (!q) return true;
      return (
        g.name.toLowerCase().includes(q) ||
        g.desc.toLowerCase().includes(q) ||
        g.purpose.toLowerCase().includes(q) ||
        // Searching a person's name to find their groups is the whole point of
        // having the membership here rather than a count.
        g.people.some((p) => p.name.toLowerCase().includes(q) || p.upn.toLowerCase().includes(q))
      );
    });
  }, [rows, type, lens, query]);

  const totals = useMemo(
    () => ({
      security: rows.filter((g) => g.type === 'Security' || g.type === 'Mail-enabled security')
        .length,
      undescribed: rows.filter((g) => !g.purpose.trim()).length,
      leavers: rows.filter((g) => g.leavers.length > 0).length,
      empty: rows.filter((g) => g.memberCount === 0).length,
    }),
    [rows],
  );

  /* Arriving from a person's Groups card, which carries the display name — the
     People register stores names, not ids, so both have to match. */
  useOpenRecord(
    rows,
    (group, value) => group.id === value || group.name.toLowerCase() === value.toLowerCase(),
    setSelected,
  );

  const save = useMutation({

    meta: { ok: 'Group saved' },
    mutationFn: (values: Record<string, unknown>) =>
      svc.groups.update(selected!.id, values as Partial<IGroup>),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.groups });
      void qc.invalidateQueries({ queryKey: qk.audit });
      setSelected(null);
    },
  });

  const loading = groups.isLoading || people.isLoading;
  const failed = [groups, people].find((q) => q.isError);

  const columns: Column<Row>[] = [
    {
      key: 'name',
      header: 'Group',
      sortBy: (g) => g.name,
      render: (g) => (
        <div className={s.person}>
          <span className={s.chip}>
            <i className="ti ti-users-group" aria-hidden />
          </span>
          <div>
            <div className={s.cellMain}>{g.name}</div>
            <div className={s.cellSub}>{g.desc || g.email || '—'}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      width: '170px',
      sortBy: (g) => g.type,
      render: (g) => (
        <>
          <Badge tone={TYPE_TONE[g.type] ?? 'muted'}>{g.type}</Badge>
          {/* Same constraint as departments: mastered on-prem, so it cannot be
              changed in Entra and nothing here should suggest otherwise. */}
          {g.onPrem && (
 <div className={`${s.cellSub} ${s.spacedTight}`} >
              On-prem AD
            </div>
          )}
        </>
      ),
    },
    {
      key: 'members',
      header: 'Members',
      width: '150px',
      sortBy: (g) => g.memberCount,
      render: (g) =>
        g.memberCount === 0 ? (
          <span className={s.cellSub}>Empty</span>
        ) : (
          <>
            <div className={s.cellMain}>{g.memberCount}</div>
            {g.unresolved.length > 0 && (
              <div className={s.cellSub}>{g.unresolved.length} not in the register</div>
            )}
          </>
        ),
    },
    {
      key: 'leavers',
      priority: 3,
      header: 'Leavers still in',
      sortBy: (g) => g.leavers.length,
      /* The one number on this screen that is an action rather than a fact. A
         leaver in a security group still has whatever it grants. */
      render: (g) =>
        g.leavers.length > 0 ? (
          <Badge tone="bad">{g.leavers.length}</Badge>
        ) : (
          <span className={s.cellSub}>—</span>
        ),
    },
    {
      key: 'purpose',
      priority: 2,
      header: 'What it is for',
      sortBy: (g) => (g.purpose.trim() ? 0 : 1),
      render: (g) =>
        g.purpose.trim() ? (
          <span className={s.cellSub}>{g.purpose}</span>
        ) : (
          <Badge tone="warn" title="Nobody has recorded what this group grants.">
            Not described
          </Badge>
        ),
    },
  ];

  return (
    <>
      <div className={s.head}>
        <div className={s.grow}>
          <h1>Groups</h1>
          <p>
            Every Entra group and who is in it. Membership syncs from Microsoft 365 nightly and
            is read-only here.
          </p>
        </div>
      </div>

      {failed && <LoadError error={failed.error} what="groups" />}

      <StatGrid>
        <StatCard
          icon="ti-users-group"
          label="Groups"
          value={loading ? '—' : String(rows.length)}
          tone="info"
          foot={loading ? '' : `${totals.security} grant access`}
        />
        <StatCard
          icon="ti-help-octagon"
          label="Not described"
          value={loading ? '—' : String(totals.undescribed)}
          tone={totals.undescribed > 0 ? 'warn' : 'ok'}
          foot={totals.undescribed > 0 ? 'Nobody has said what they open' : 'All accounted for'}
        />
        <StatCard
          icon="ti-user-off"
          label="Groups holding leavers"
          value={loading ? '—' : String(totals.leavers)}
          tone={totals.leavers > 0 ? 'bad' : 'ok'}
          foot={totals.leavers > 0 ? 'Access not yet removed' : 'None outstanding'}
        />
        <StatCard
          icon="ti-circle-off"
          label="Empty groups"
          value={loading ? '—' : String(totals.empty)}
          tone={totals.empty > 0 ? 'warn' : 'ok'}
          foot="Nothing in them — candidates to retire"
        />
      </StatGrid>

      <Controls count={`${filtered.length} of ${rows.length}`}>
        <SearchBox value={query} onChange={setQuery} placeholder="Group, or a person in one…" />
        <Select
          label="Type"
          value={type}
          onChange={setType}
          options={[
            { value: 'all', label: 'All types' },
            ...GROUP_TYPES.map((t) => ({ value: t, label: t })),
          ]}
        />
        <Segmented
          label="Lens"
          value={lens}
          onChange={setLens}
          options={[
            { value: 'all', label: 'All' },
            { value: 'undescribed', label: 'Not described' },
            { value: 'leavers', label: 'Holding leavers' },
            { value: 'empty', label: 'Empty' },
            { value: 'unowned', label: 'No owner' },
          ]}
        />
      </Controls>

      <Panel flush>
        {loading ? (
          <TableSkeleton />
        ) : (
          <DataTable
            rows={filtered}
            columns={columns}
            rowKey={(g) => g.id}
            initialSort={{ key: 'members', dir: 'desc' }}
            onRowClick={setSelected}
            selectedKey={selected?.id}
            empty={
              <EmptyState icon="ti-users-group" title="No groups match">
                {lens === 'all' && type === 'all' && !query
                  ? 'Nothing in the group register yet. The nightly sync fills this from /groups.'
                  : 'Nothing under this filter.'}
              </EmptyState>
            }
          />
        )}
      </Panel>

      <EditDrawer
        open={selected !== null}
        record={selected}
        fields={groupFields()}
        lookups={{ people: people.data ?? [], suppliers: [] }}
        noun="group"
        icon="ti-users-group"
        title={selected?.name}
        meta={
          selected && (
            <>
              <Badge tone={TYPE_TONE[selected.type] ?? 'muted'}>{selected.type}</Badge>
              <Badge tone="muted">{selected.memberCount} members</Badge>
              {selected.onPrem && <Badge tone="info">On-prem AD</Badge>}
              {selected.leavers.length > 0 && (
                <Badge tone="bad">{selected.leavers.length} leavers still in</Badge>
              )}
            </>
          )
        }
        onClose={() => setSelected(null)}
        onSave={(values) => save.mutate(values)}
        saving={save.isPending}
        error={save.error}
      >
        {selected && (
          <>
            <SummaryTiles>
              <SummaryTile icon="ti-users" value={selected.memberCount} label="Members" />
              <SummaryTile
                icon="ti-user-check"
                value={selected.owners.length || 'None'}
                label="Owners"
                /* Nobody accountable for a group's membership is the finding, not
                   a neutral zero. */
                tone={selected.owners.length === 0 ? 'warn' : 'muted'}
              />
              <SummaryTile
                icon="ti-user-off"
                value={selected.leavers.length}
                label="Leavers in"
                tone={selected.leavers.length > 0 ? 'bad' : 'ok'}
              />
              <SummaryTile
                icon={selected.purpose.trim() ? 'ti-checkbox' : 'ti-help-octagon'}
                value={selected.purpose.trim() ? 'Yes' : 'No'}
                label="Described"
                tone={selected.purpose.trim() ? 'ok' : 'warn'}
              />
            </SummaryTiles>

            <KeyValue
              rows={[
                { key: 'Type', value: selected.type },
                { key: 'Members', value: String(selected.memberCount) },
                { key: 'Mail', value: selected.email || '—' },
                {
                  key: 'Mastered in',
                  value: selected.onPrem ? 'On-prem AD' : 'Entra',
                },
                {
                  key: 'Last synced',
                  value: selected.lastSyncedAt ? fmtDate(selected.lastSyncedAt) : '—',
                },
              ]}
            />

            <Explain title="Why membership cannot be edited here">
              This register records access, it does not grant it. Removing somebody here would
              write to a SharePoint list, not to Entra — they would keep whatever the group opens,
              and the 02:00 sync would put the row back unchanged. Membership is changed in Entra,
              or in on-prem AD for the groups marked as mastered there.
            </Explain>

            {selected.rule && (
              <div>
                <div className={s.sectionLabel}>Membership rule</div>
                {/* For a dynamic group the rule IS the membership — a member list
                    without it tells you who is in the group but never why. */}
                <div className={`${s.ownedNote} ${s.mono}`}>{selected.rule}</div>
              </div>
            )}

            {/* Never collapsed. Every other section is a fact; this one is a job
                somebody has not finished, and folding it away by default would
                be the app deciding that is fine. */}
            {selected.leavers.length > 0 && (
              <div>
                <div className={s.sectionLabel}>Leavers still in this group</div>
                <div className={s.rows}>
                  {selected.leavers.map((p) => (
                    <Link
                      key={p.id}
                      to={recordHref('/people', p.id)}
                      className={`${s.row} ${s.rowFlush}`}
                    >
                      <span className={s.rowIcon}>
                        <i className="ti ti-user-off" aria-hidden />
                      </span>
                      <div className={s.rowMain}>
                        <div className={s.rowTitle}>{p.name}</div>
                        <div className={s.rowSub}>{p.upn}</div>
                      </div>
                      <Badge tone="bad">Leaver</Badge>
                      <i className="ti ti-chevron-right" aria-hidden />
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <Section
              title={selected.owners.length > 0 ? 'Owners' : 'No owner recorded'}
              count={selected.owners.length}
              defaultOpen={selected.owners.length === 0 || selected.owners.length <= 5}
            >
              {selected.owners.length > 0 ? (
                <div className={s.rows}>
                  {selected.owners.map((o) => (
                    <div key={o.id} className={`${s.row} ${s.rowFlush}`}>
                      <span className={s.rowIcon}>
                        <i className="ti ti-user-check" aria-hidden />
                      </span>
                      <div className={s.rowMain}>
                        <div className={s.rowTitle}>{o.name || o.id}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={s.cellSub}>
                  Nobody in Entra is accountable for this group's membership.
                </div>
              )}
            </Section>

            <Section
              title="Members"
              count={selected.memberCount}
              /* Open only when the list is short enough to be worth seeing at
                 once. SG-All-Staff has the whole company in it, and opening that
                 by default is how the drawer became nine thousand pixels tall. */
              defaultOpen={selected.memberCount <= 12}
            >
              {selected.people.length === 0 && selected.unresolved.length === 0 ? (
                <div className={s.cellSub}>Nothing in this group.</div>
              ) : (
                <div className={s.rows}>
                  <Capped cap={8} noun="all">
                  {selected.people.map((p) => (
                    <Link
                      key={p.id}
                      to={recordHref('/people', p.id)}
                      className={`${s.row} ${s.rowFlush}`}
                    >
                      <span className={s.rowIcon}>
                        <i className="ti ti-user" aria-hidden />
                      </span>
                      <div className={s.rowMain}>
                        <div className={s.rowTitle}>{p.name}</div>
                        <div className={s.rowSub}>{p.upn}</div>
                      </div>
                      {p.status !== 'Active' && <Badge tone="warn">{p.status}</Badge>}
                      <i className="ti ti-chevron-right" aria-hidden />
                    </Link>
                  ))}
                  {/* Nested groups and anything the user sync excludes — guests,
                      most obviously. Shown rather than dropped: a member you
                      cannot see is access you cannot audit. */}
                  {selected.unresolved.map((m) => (
                    <div key={m.id} className={`${s.row} ${s.rowFlush}`}>
                      <span className={s.rowIcon}>
                        <i
                          className={`ti ${m.kind === 'group' ? 'ti-users-group' : 'ti-user-question'}`}
                          aria-hidden
                        />
                      </span>
                      <div className={s.rowMain}>
                        <div className={s.rowTitle}>{m.name || m.id}</div>
                        <div className={s.rowSub}>
                          {m.kind === 'group'
                            ? 'Nested group — its members get this too'
                            : 'Not in the people register'}
                        </div>
                      </div>
                      <Badge tone={m.kind === 'group' ? 'info' : 'muted'}>
                        {m.kind === 'group' ? 'Group' : 'Unmatched'}
                      </Badge>
                    </div>
                  ))}
                  </Capped>
                </div>
              )}
            </Section>
          </>
        )}
      </EditDrawer>
    </>
  );
}
